/**
 * [BLURY] | 🥭 Stage
 * The preview area: a ratio-fitted frame holding the output canvas and its
 * handle overlay.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { useRef, type MouseEvent } from 'react';
import { useCanvasRenderer } from '@/hooks/useCanvasRenderer';
import { useFittedFrame } from '@/hooks/useFittedFrame';
import { useStudio } from '@/state/useStudio';
import { Handles } from './Handles';
import styles from './Stage.module.css';

export function Stage() {
  const size = useStudio(s => s.size);
  const previewSize = useStudio(s => s.previewSize);
  const previewScale = useStudio(s => s.previewScale);
  const addShape = useStudio(s => s.addShape);

  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useCanvasRenderer(canvasRef);
  const frame = useFittedFrame(stageRef, previewSize);

  const onDoubleClick = (event: MouseEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    addShape({
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    });
  };

  return (
    <main className={styles.stage} ref={stageRef}>
      <p className={styles.label}>
        Output · {size.width} × {size.height}
        {previewScale > 1 && (
          <span className={styles.sub}> · preview at 1/{previewScale.toFixed(1)}</span>
        )}
      </p>

      <div
        className={styles.frame}
        ref={frameRef}
        style={{ width: frame.width, height: frame.height }}
      >
        <canvas id="out" className={styles.canvas} ref={canvasRef} onDoubleClick={onDoubleClick} />
        <Handles frameRef={frameRef} />
      </div>

      <p className={styles.hint}>
        <b>drag</b> a handle to move a shape · <b>double-click</b> canvas to add
        <br />
        <b>alt-click</b> a handle to delete · <b>scroll</b> over a handle to resize
      </p>
    </main>
  );
}
